/**
 * DashboardService — KPI and stage rollup metrics for deal home
 */

import { DealService } from './DealService.js';
import { GoalService } from './GoalService.js';
import { UserService } from './UserService.js';
import { CommissionService } from './CommissionService.js';
import { STAGE_ORDER, STAGE_META } from '../constants/Stages.js';
import { auth } from './AuthService.js';
import { permissions } from './PermissionService.js';
import { filterDealsByScope } from '../utils/CrmDealFilters.js';
import { ApiClient } from './ApiClient.js';

export class DashboardService {
    static _myDashboardsCache = null;
    static _myDashboardsCacheKey = null;

    static async getMyDashboards() {
        const session = auth.getSession();
        const cacheKey = session?.userId || session?.role || 'anon';
        if (DashboardService._myDashboardsCache && DashboardService._myDashboardsCacheKey === cacheKey) {
            return DashboardService._myDashboardsCache;
        }
        try {
            const data = await ApiClient.get('/api/v1/dashboard/my');
            const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
            DashboardService._myDashboardsCache = list;
            DashboardService._myDashboardsCacheKey = cacheKey;
            return list;
        } catch (err) {
            // Fallback to legacy role-driven config if API not yet seeded or unavailable
            const { getDashboardConfig } = await import('../constants/DashboardConfig.js');
            const role = session?.role || 'viewer';
            const cfg = getDashboardConfig(role);
            const fallback = [{ key: cfg.title.toLowerCase().replace(/\s+/g, '_'), label: cfg.title, widgets: cfg.widgets || [], is_default: true }];
            return fallback;
        }
    }

    static async getAllDashboards() {
        try {
            const data = await ApiClient.get('/api/v1/dashboard/all');
            const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
            return Array.isArray(list) ? list : [];
        } catch {
            return [];
        }
    }

    static clearMyDashboardsCache() {
        DashboardService._myDashboardsCache = null;
        DashboardService._myDashboardsCacheKey = null;
    }

    static async getMetrics(filters = {}) {
        let deals = await DealService.getListView();
        deals = filterDealsByScope(deals, filters);

        const openDeals = deals.filter(d => d.status === 'open');
        const wonDeals = deals.filter(d => d.status === 'won');
        const lostDeals = deals.filter(d => d.status === 'lost');

        const pipelineValue = openDeals.reduce((s, d) => s + (d.amount || 0), 0);
        const forecastRevenue = openDeals.reduce((s, d) => s + (d.forecastAmount || 0), 0);
        const closedWon = wonDeals.reduce((s, d) => s + (d.amount || 0), 0);
        const closedLost = lostDeals.reduce((s, d) => s + (d.amount || 0), 0);

        const session = auth.getSession();
        const year = new Date().getFullYear();
        let goalProgress = null;
        let commissionYtd = 0;

        if (session) {
            const curMonth = new Date().getMonth() + 1;
            const monthGoals = await GoalService.getForUserMonth(session.userId, year, curMonth);
            const revGoal = monthGoals.find(g => g.goalType === 'revenue' && g.status === 'active');
            const myWon = wonDeals.filter(d => d.ownerUserId === session.userId);
            const myClosed = myWon.reduce((s, d) => s + (d.amount || 0), 0);
            const target = revGoal ? Number(revGoal.targetValue) || 0 : 0;
            if (target > 0) {
                goalProgress = {
                    closed: myClosed,
                    target,
                    pct: Math.round((myClosed / target) * 100)
                };
            }
            const commissions = await CommissionService.getAll();
            commissionYtd = CommissionService.ytdTotal(commissions, session.userId, year);
        }

        const byStage = STAGE_ORDER.map(stage => {
            const stageDeals = deals.filter(d => d.dealStage === stage);
            return {
                stage,
                label: STAGE_META[stage]?.label || stage,
                count: stageDeals.length,
                amount: stageDeals.reduce((s, d) => s + (d.amount || 0), 0),
                forecast: stageDeals.reduce((s, d) => s + (d.forecastAmount || 0), 0)
            };
        });

        return {
            totalDeals: deals.length,
            pipelineValue,
            forecastRevenue,
            closedWon,
            closedLost,
            goalProgress,
            commissionYtd,
            byStage,
            canViewOrg: permissions.canViewOrgDeals()
        };
    }

    static async getMetricsRaw() {
        try {
            const res = await ApiClient.get('/api/v1/dashboard/metrics');
            return res?.data ?? res ?? null;
        } catch { return null; }
    }
    static async getAvgDaysInStage() {
        try {
            const res = await ApiClient.get('/api/v1/dashboard/widget-data/avg_days_in_stage');
            return res?.data ?? res ?? [];
        } catch { return []; }
    }
    static async getWinRateBySource() {
        try {
            const res = await ApiClient.get('/api/v1/dashboard/widget-data/win_rate_by_source');
            return res?.data ?? res ?? [];
        } catch { return []; }
    }
    static async getLostByCategory() {
        try {
            const res = await ApiClient.get('/api/v1/dashboard/widget-data/lost_by_category');
            return res?.data ?? res ?? [];
        } catch { return []; }
    }

    static async getGoalMetrics(year) {
        const allGoals = await GoalService.getAll();
        const yearGoals = allGoals.filter(g => g.year === (year || new Date().getFullYear()) && g.status !== 'inactive');
        const users = await UserService.getLookupUsers().catch(() => []);
        const userMap = Object.fromEntries(users.map(u => [u.id, u]));

        const enriched = yearGoals.map(g => {
            const pct = g.targetValue > 0 ? Math.min(100, Math.round((g.achievedValue / g.targetValue) * 100)) : 0;
            return {
                ...g,
                userName: userMap[g.userId]?.name || '—',
                progress: pct,
                isBehind: pct < 50 && g.status !== 'completed' && g.status !== 'paused',
                isOnTrack: pct >= 50 && pct < 100 && g.status !== 'completed',
                isCompleted: g.status === 'completed' || pct >= 100
            };
        });

        const totalTarget = enriched.reduce((s, g) => s + (g.targetValue || 0), 0);
        const totalAchieved = enriched.reduce((s, g) => s + (g.achievedValue || 0), 0);
        const summaryPct = totalTarget > 0 ? Math.round((totalAchieved / totalTarget) * 100) : 0;
        const atRiskCount = enriched.filter(g => g.isBehind).length;
        const completedCount = enriched.filter(g => g.isCompleted).length;

        const byUser = {};
        enriched.forEach(g => {
            if (!byUser[g.userId]) byUser[g.userId] = { userId: g.userId, userName: g.userName, totalTarget: 0, totalAchieved: 0, goals: [] };
            byUser[g.userId].totalTarget += g.targetValue || 0;
            byUser[g.userId].totalAchieved += g.achievedValue || 0;
            byUser[g.userId].goals.push(g);
        });
        Object.values(byUser).forEach(u => {
            u.progress = u.totalTarget > 0 ? Math.min(100, Math.round((u.totalAchieved / u.totalTarget) * 100)) : 0;
        });

        const topPerformers = Object.values(byUser)
            .filter(u => u.progress > 0)
            .sort((a, b) => b.progress - a.progress);

        const atRiskUsers = Object.values(byUser)
            .filter(u => u.progress < 50 && u.progress > 0)
            .sort((a, b) => a.progress - b.progress);

        return {
            totalTarget,
            totalAchieved,
            summaryPct,
            atRiskCount,
            completedCount,
            totalGoals: enriched.length,
            enriched,
            byUser: Object.values(byUser),
            topPerformers,
            atRiskUsers
        };
    }
}
